import React from 'react';
import {
  Image,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { fpColors } from '../../../styles/theme';

/**
 * FE-209 — ItemDetail "From this scan" card.
 *
 * When `item.source_file_id` is set the item was scanned as part of a
 * multi-item Scene. This card links back to the SceneViewer so the
 * user can see the source photo, all the items detected from it, and
 * the bbox overlays.
 *
 * Minimal viable version:
 *   - thumbnail: `source_image_url` (the source scan's room photo,
 *     already on the inventory response)
 *   - title: "From this scan"
 *   - subtitle: (optional — only shown if we know the count; we
 *     don't have it on the item response yet, so omitted in v1)
 *   - chevron: tappable → SceneViewer(fileId)
 *
 * Hidden when `sourceFileId` is null (Quick-Add items, manual entries,
 * legacy rows without a scan source).
 *
 * Future enrichment: hit `GET /users/files/{id}` on mount to populate
 * the scene's AI label + item count for the subtitle. Skipped in v1
 * to keep this a pure render with no extra network call.
 */

interface Props {
  sourceFileId: number | null | undefined;
  sourceImageUrl: string | null | undefined;
}

export const ItemSourceScanCard: React.FC<Props> = ({
  sourceFileId,
  sourceImageUrl,
}) => {
  const navigation = useNavigation<any>();

  if (sourceFileId == null) return null;

  return (
    <Pressable
      onPress={() =>
        navigation.navigate('SceneViewer', { fileId: sourceFileId })
      }
      style={({ pressed }) => [
        styles.card,
        pressed && { opacity: 0.92 },
      ]}
      accessibilityRole="button"
      accessibilityLabel="View the scan this item was detected in"
      testID="item-detail-scan-link"
    >
      <View style={styles.header}>
        <MaterialIcons
          name="photo-camera"
          size={18}
          color={fpColors.primary}
        />
        <Text style={styles.title}>From this scan</Text>
      </View>

      <View style={styles.row}>
        <View style={styles.thumbWrap}>
          {sourceImageUrl ? (
            <Image
              source={{ uri: sourceImageUrl }}
              style={styles.thumb}
              accessibilityIgnoresInvertColors
            />
          ) : (
            <View style={[styles.thumb, styles.thumbPlaceholder]}>
              <MaterialIcons
                name="image"
                size={20}
                color={fpColors.outline}
              />
            </View>
          )}
        </View>
        <View style={styles.body}>
          <Text style={styles.bodyTitle} numberOfLines={1}>
            View source scan
          </Text>
          <Text style={styles.bodySubtitle} numberOfLines={1}>
            See the photo this item was detected in
          </Text>
        </View>
        <MaterialIcons
          name="chevron-right"
          size={22}
          color={fpColors.outline}
        />
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  thumbWrap: {
    width: 56,
    height: 56,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: fpColors.surface,
  },
  thumb: { width: '100%', height: '100%' },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: { flex: 1, minWidth: 0 },
  bodyTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  bodySubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
});

export default ItemSourceScanCard;
